import { isEmpty, noop } from 'lodash'
import { NumericFilterInputPropsType } from '../../../utils/types/tableFilterTypes'
import { useEffect, useRef, useState } from 'react'
import { filterOperatorValues } from '../../../utils'
import { SvgCross, SvgFunnel } from '../../../assets/images'

export const NumericFilterInput = ({
  handleFilterChange = noop,
  search_key,
  filterObject,
  dataQaId,
  filterOperator,
  columnHeaderRef,
  innerInputRef,
  tabIndex,
}: NumericFilterInputPropsType) => {
  const [inputValue, setInputValue] = useState('')
  const [inputToValue, setInputToValue] = useState('')
  const [isFilterApplied, setIsFilterApplied] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const inputToRef = useRef<HTMLInputElement | null>(null)

  const isRangeOperator = () => {
    return (
      filterOperator?.value === filterOperatorValues.BETWEEN ||
      filterOperator?.value === filterOperatorValues.NOT_BETWEEN
    )
  }

  const isNullOrNotNull = () => {
    return (
      filterOperator?.value === filterOperatorValues.IS_NULL ||
      filterOperator?.value === filterOperatorValues.IS_NOT_NULL
    )
  }

  const onFilterChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e?.key === 'Enter') {
      ;(e.target as HTMLInputElement).blur()
    }
  }

  const handleFocusOut = () => {
    if (isRangeOperator()) {
      if (
        !isEmpty(inputRef.current?.value) &&
        !isEmpty(inputToRef.current?.value)
      ) {
        const selectedValue = [inputRef.current?.value ?? '']
        selectedValue.push(inputToRef?.current?.value ?? '')
        handleFilterChange({
          search_key: search_key,
          operator: filterOperator?.value ?? '',
          search_value: selectedValue,
        })
      }
    } else if (!isEmpty(inputRef.current?.value)) {
      const selectedValue = [inputRef.current?.value ?? '']
      if (
        selectedValue[0] !==
          filterObject?.search_criteria?.[search_key]?.search_value?.[0] ||
        filterOperator?.value !==
          filterObject?.search_criteria?.[search_key]?.operator
      ) {
        handleFilterChange({
          search_key: search_key,
          operator: filterOperator?.value ?? '',
          search_value: selectedValue,
        })
      }
    } else {
      if (filterObject?.search_criteria?.[search_key]?.search_value?.length)
        handleFilterChange({
          search_key: search_key,
          operator: filterOperator?.value ?? '',
          search_value: [],
        })
    }
  }

  const handleClearFilterButtonClick = (firstCrossClicked?: boolean) => {
    if (isRangeOperator()) {
      if (firstCrossClicked) {
        setInputValue('')
        if (inputRef.current) inputRef.current.value = ''
      } else {
        setInputToValue('')
        if (inputToRef.current) inputToRef.current.value = ''
      }
      if (!isFilterApplied) {
        return
      }
      if (!inputRef?.current?.value && !inputToRef?.current?.value) {
        handleFilterChange({
          search_key: search_key,
          operator: filterOperator?.value ?? '',
          search_value: [],
        })
      }
    } else {
      setInputValue('')
      if (inputRef.current) inputRef.current.value = ''
      if (!isFilterApplied) {
        return
      }
      handleFilterChange({
        search_key: search_key,
        operator: filterOperator?.value ?? '',
        search_value: [],
      })
    }
  }

  useEffect(() => {
    if (isNullOrNotNull()) {
      if (inputRef.current) {
        inputRef.current.disabled = true
        inputRef.current.value = ''
        setInputValue('')
      }
      handleFilterChange({
        search_key: search_key,
        operator: filterOperator?.value ?? '',
        search_value: [''],
      })
    } else if (inputRef.current) inputRef.current.disabled = false
  }, [filterOperator])

  useEffect(() => {
    if (
      filterObject &&
      search_key &&
      filterObject?.search_criteria?.[search_key]
    ) {
      setIsFilterApplied(true)
    } else setIsFilterApplied(false)
  }, [filterObject])

  useEffect(() => {
    innerInputRef.current = inputRef.current
  }, [])

  useEffect(() => {
    const v1 = filterObject?.search_criteria?.[search_key]?.search_value?.[0]
    const v2 = filterObject?.search_criteria?.[search_key]?.search_value?.[1]
    setInputValue(v1 !== undefined && v1 !== null ? String(v1) : '')
    setInputToValue(v2 !== undefined && v2 !== null ? String(v2) : '')
  }, [filterObject])

  return (
    <>
      <style>
        {`
          input[type="number"]::-webkit-inner-spin-button,
          input[type="number"]::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          input[type="number"] {
            -moz-appearance: textfield;
          }
        `}
      </style>
      <div
        className="flex items-center flex-1"
        onBlur={(e) => {
          if (
            !e.relatedTarget ||
            !columnHeaderRef.current?.contains(e.relatedTarget)
          )
            handleFocusOut()
        }}
      >
        <div
          className={`relative flex items-center ${
            isRangeOperator() ? '' : 'w-full'
          }`}
        >
          <div className="absolute left-1 mt-[1px] px-1">
            <SvgFunnel
              width={'16'}
              height={'16'}
              className={`cursor-default ${
                isFilterApplied
                  ? 'svg-path-fill-secondary'
                  : 'svg-path-fill-accent-300'
              }
              `}
            />
          </div>
          <input
            data-qa-id={`${dataQaId}_input_1`}
            type="number"
            className={`w-full ${
              isRangeOperator() ? 'min-w-[3.8rem]' : 'min-w-32'
            } flex-1 border border-gray-300 rounded focus:outline-none focus:border-secondary-500 hover:border-secondary-500  text-xs font-light pl-7 pr-8 pt-2 pb-2`}
            onKeyDown={onFilterChange}
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            tabIndex={tabIndex}
          />
          {isNullOrNotNull() || inputValue ? (
            <div className="absolute right-2 px-1">
              <SvgCross
                width={'10'}
                height={'10'}
                className={
                  'cross-icon svg-path-fill-secondary focus:outline-none'
                }
                onClick={(_e) => {
                  handleClearFilterButtonClick(true)
                  if (inputRef.current) inputRef.current.focus()
                }}
                tabIndex={-1}
              />
            </div>
          ) : null}
        </div>

        {isRangeOperator() ? (
          <>
            <div className="p-1 font-light">-</div>
            <div className="relative flex items-center ">
              <div className="absolute left-1 mt-[1px] px-1">
                <SvgFunnel
                  width={'16'}
                  height={'16'}
                  className={`cursor-default ${
                    isFilterApplied
                      ? 'svg-path-fill-secondary'
                      : 'svg-path-fill-accent-300'
                  }
                  `}
                />
              </div>
              <input
                data-qa-id={`${dataQaId}_input_2`}
                type="number"
                className="w-full min-w-[3.8rem] flex-1 border border-gray-300 rounded focus:outline-none focus:border-secondary-500 hover:border-secondary-500 text-xs font-light pl-7 pr-8 pt-2 pb-2"
                onKeyDown={onFilterChange}
                ref={inputToRef}
                value={inputToValue}
                onChange={(e) => setInputToValue(e.target.value)}
              />
              {inputToValue && (
                <div className="absolute right-2 px-1">
                  <SvgCross
                    width={'10'}
                    height={'10'}
                    className={'svg-path-fill-secondary focus:outline-none'}
                    onClick={(_e) => {
                      handleClearFilterButtonClick(false)
                      if (inputToRef.current) inputToRef.current.focus()
                    }}
                    tabIndex={-1}
                  />
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </>
  )
}
